import React, { useState, useMemo } from "react";
import {
  Table,
  Button,
  Badge,
  ButtonGroup,
  Modal,
  Form,
} from "react-bootstrap";
import moment from "moment";
import {
  FaList,
  FaThLarge,
  FaDownload,
  FaEye,
  FaTrash,
} from "react-icons/fa";
import DocumentUploadModal from "./DocumentUploadModal";
import PreviewDocument from "./PreviewDocument";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config.json";
import { useCurrentUser } from "../../hooks/useCurrentUser";

export const getDocumentTitle = (doc) => {
  if (doc?.documentMetadata?.name) return doc.documentMetadata.name;
  if (doc?.fileMetadata?.fileName) return doc.fileMetadata.fileName;
  return "Untitled document";
};

export const getUploadedBy = (doc) => {
  if (!doc?.createdBy) return "—";

  const { first_name, last_name, email } = doc.createdBy;

  if (first_name || last_name) {
    return `${first_name ?? ""} ${last_name ?? ""}`.trim();
  }

  return email ?? "—";
};

const DocumentList = ({ documents = [], onRefresh }) => {
  const { isHead } = useCurrentUser();
  const [showUpload, setShowUpload] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const filteredDocuments = useMemo(() => {
    return documents.filter((d) => {
      const title = getDocumentTitle(d).toLowerCase();

      if (search && !title.includes(search.toLowerCase())) return false;

      if (fromDate || toDate) {
        const created = moment(d.createdAt);
        if (fromDate && created.isBefore(moment(fromDate), "day"))
          return false;
        if (toDate && created.isAfter(moment(toDate), "day")) return false;
      }

      return true;
    });
  }, [documents, search, fromDate, toDate]);

  const confirmDelete = async () => {
    if (!deleteDoc) return;

    setDeleting(true);

    try {
      await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}documents/${deleteDoc.id}`,
        {},
        {},
        "DELETE"
      );

      setDeleteDoc(null);
      onRefresh();
    } catch (error) {
      console.error("Error deleting document:", error);
      // Optionally show error notification to user
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1">ESG Documents</h3>
          <small className="text-muted">
            Central repository for ESG-related files
          </small>
        </div>

        <div className="d-flex align-items-center gap-2">
          <ButtonGroup>
            <Button
              variant={viewMode === "grid" ? "primary" : "outline-secondary"}
              onClick={() => setViewMode("grid")}
              aria-label="Grid view"
            >
              <FaThLarge />
            </Button>
            <Button
              variant={viewMode === "table" ? "primary" : "outline-secondary"}
              onClick={() => setViewMode("table")}
              aria-label="Table view"
            >
              <FaList />
            </Button>
            
          </ButtonGroup>

          {isHead && (
            <Button variant="primary" onClick={() => setShowUpload(true)}>
              Upload Document
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="border rounded-3 p-3 mb-4 bg-light">
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <Form.Label className="fw-semibold">Document name</Form.Label>
            <Form.Control
              placeholder="Search documents…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <Form.Label className="fw-semibold">Uploaded from</Form.Label>
            <Form.Control
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={toDate || undefined}
            />
          </div>

          <div className="col-md-3">
            <Form.Label className="fw-semibold">Uploaded to</Form.Label>
            <Form.Control
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate || undefined}
            />
          </div>

          <div className="col-md-2">
            <Button
              variant="outline-secondary"
              className="w-100"
              onClick={() => {
                setSearch("");
                setFromDate("");
                setToDate("");
              }}
              disabled={!search && !fromDate && !toDate}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredDocuments.length === 0 && (
        <div className="text-center py-5">
          <p className="text-muted">
            {documents.length === 0
              ? "No documents uploaded yet"
              : "No documents match your filters"}
          </p>
          {documents.length === 0 && isHead && (
            <Button variant="primary" onClick={() => setShowUpload(true)}>
              Upload Your First Document
            </Button>
          )}
        </div>
      )}

      {/* Table */}
      {viewMode === "table" && filteredDocuments.length > 0 && (
        <Table hover responsive className="align-middle">
          <thead className="table-light">
            <tr>
              <th>Document</th>
              <th>Uploaded By</th>
              <th>Uploaded</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDocuments.map((doc) => (
              <tr key={doc.id}>
                <td className="fw-semibold">{getDocumentTitle(doc)}</td>

                <td className="text-muted">{getUploadedBy(doc)}</td>

                <td>{moment(doc.createdAt).format("DD MMM YYYY")}</td>

                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="me-2"
                    onClick={() => setPreviewDoc(doc)}
                    aria-label="Preview document"
                  >
                    <FaEye />
                  </Button>

                  <a
                    href={doc.fileMetadata.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary me-2"
                    aria-label="Download document"
                  >
                    <FaDownload />
                  </a>

                  {isHead && (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => setDeleteDoc(doc)}
                      aria-label="Delete document"
                    >
                      <FaTrash />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Grid */}
      {viewMode === "grid" && filteredDocuments.length > 0 && (
        <div
          className="d-grid gap-3"
          style={{
            gridTemplateColumns: "repeat(3, 1fr)",
          }}
        >
          {filteredDocuments.map((doc) => (
            <div
              key={doc.id}
              className="border rounded-3 bg-white p-3"
              style={{
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              }}
            >
              {/* Document Icon and Name */}
              <div className="d-flex align-items-start gap-2 mb-3">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: "#E3F2FD",
                    borderRadius: "8px",
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    fill="#1976D2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
                    <path d="M14 2v6h6" fill="none" stroke="#1976D2" strokeWidth="2" />
                  </svg>
                </div>
                <div className="flex-grow-1 min-w-0">
                  <div
                    className="fw-semibold mb-0"
                    style={{
                      fontSize: "0.95rem",
                      lineHeight: "1.3",
                      display: "-webkit-box",
                   
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: "100%",
                    }}
                    title={getDocumentTitle(doc)}
                  >
                    {getDocumentTitle(doc).length > 500 ? getDocumentTitle(doc).slice(0, 500) + '...' : getDocumentTitle(doc).slice(0, 500)}
                  </div>
                 
                </div>
              </div>

              {/* Uploaded By */}
              <div className="mb-2">
                <div
                  className="text-uppercase mb-1"
                  style={{
                    fontSize: "0.65rem",
                    color: "#999",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                  }}
                >
                  UPLOADED BY
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "#333",
                  }}
                >
                  {getUploadedBy(doc)}
                </div>
              </div>

              {/* Upload Date */}
              <div className="mb-3">
                <div
                  className="text-uppercase mb-1"
                  style={{
                    fontSize: "0.65rem",
                    color: "#999",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                  }}
                >
                  UPLOAD DATE
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "#333",
                  }}
                >
                  {moment(doc.createdAt).format("DD MMM YYYY")}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-2 align-items-center">
                <Button
                
                  variant="primary"
                  size="sm"
                  onClick={() => setPreviewDoc(doc)}
                  style={{
                    padding: "8px 12px",
                  }}
                >
                  <FaEye size={14} />
           
                </Button>

                <Button
                  variant="outline-secondary"
                  size="sm"
                  as="a"
                  href={doc.fileMetadata.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download document"
                  style={{
                    padding: "8px 12px",
                  }}
                >
                  <FaDownload size={14} />
                </Button>

                {isHead && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => setDeleteDoc(doc)}
                    aria-label="Delete document"
                    style={{
                      padding: "8px 12px",
                    }}
                  >
                    <FaTrash size={14} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <DocumentUploadModal
          show={showUpload}
          onClose={() => setShowUpload(false)}
          onSuccess={onRefresh}
        />
      )}

      {previewDoc && (
        <PreviewDocument
          document={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {deleteDoc && (
        <Modal show centered onHide={() => setDeleteDoc(null)}>
          <Modal.Header closeButton>
            <Modal.Title>Delete document</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Delete <strong>{getDocumentTitle(deleteDoc)}</strong>? This action
            cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setDeleteDoc(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default DocumentList;